// src/index.js

import React from 'react';
import ReactDOM from 'react-dom/client';
import { HashRouter } from 'react-router-dom';
// [변경] index.css 제거, GlobalStyle로 대체
// import './index.css';
import { ThemeProvider } from 'styled-components'; // [추가] ThemeProvider
import GlobalStyle from './styles/GlobalStyle'; // [추가] GlobalStyle
import theme from './styles/theme'; // [추가] theme
import App from './App';
import reportWebVitals from './reportWebVitals';
import './i18n';
import './utils/environment'; // 🔐 로그 제어 시스템 전역 함수 생성
// [멀티 플랫폼 지원] 플랫폼 감지 함수 import - isPiSandboxEnvironment 추가
import { getPlatform, isDevelopment, shouldLoadPiSDK, getEnvironmentInfo, debugLog, errorLog, isPiSDKReady, isPiSandboxEnvironment, enableLogFiltering } from './utils/environment';
// [🔧 수정] Analytics는 firebase.js에서 통합 관리
// [🔧 제거] versionChecker import 제거 (새로운 버전 체크 시스템으로 대체)

// 🔧 [추가] Service Worker 청소 - VersionCheck보다 먼저 실행
import { nukeServiceWorkersOnce } from './utils/swCleanup';
import PiAdapter from './adapters/PiAdapter'; // 🎯 [NEW] Adapter Import
// 🔧 [Cost Optimization 2026-03-03] warmupFunctions 제거 — Cloud Scheduler warmupPing으로 대체

// 🎯 [iOS Fix] requestIdleCallback 폴백 - iOS Safari/WKWebView에서 지원하지 않음
// 기존 유저(Android/PC)에게는 영향 없음 - 이미 지원하므로 조건문이 false로 평가됨
if (typeof window !== 'undefined' && !window.requestIdleCallback) {
  window.requestIdleCallback = function (callback, options) {
    const timeout = options?.timeout || 1;
    return setTimeout(callback, timeout);
  };
  window.cancelIdleCallback = function (id) {
    clearTimeout(id);
  };
}

// 🔧 [강화] Service Worker 청소 - 번들 시작 시 가장 먼저 실행
(async () => {
  try {
    await nukeServiceWorkersOnce();
  } catch (error) {
    console.warn('[SW-Cleanup] Initialization failed:', error);
  }
})();

// 🔧 [Cost Optimization 2026-03-03] 클라이언트 warmup 제거
// 기존: warmupFunctions()가 updatePpPoints, createSpot, purchaseDeal, purchaseTicket 4개 함수를 동시 호출
// 변경: Cloud Scheduler가 warmupPing을 5분마다 호출하여 콜드스타트 방지 (Functions 비용 ~40%↓)

const root = ReactDOM.createRoot(document.getElementById('root'));

// [2단계 개선] 환경 정보를 전역에서 사용할 수 있도록 저장
window.__APP_ENVIRONMENT__ = {
  initialized: false,
  platform: null,
  piSDKLoaded: false,
  piSDKReady: false, // [추가] SDK 준비 상태 추가
  slowLoading: false // [추가] 느린 로딩 상태 표시
};

/**
 * [2단계 개선] Pi SDK 로드 함수
 * Promise로 SDK 로드 상태를 명확하게 관리
 * [네트워크 개선] 타임아웃을 30초로 증가하여 느린 네트워크 대응
 */
const loadPiSDK = () => {
  return new Promise((resolve) => {
    debugLog('[index.js] Pi SDK 로드 시작');

    // 이미 로드되어 있는지 확인
    if (window.Pi) {
      debugLog('[index.js] Pi SDK가 이미 로드됨');
      window.__APP_ENVIRONMENT__.piSDKLoaded = true;
      // [단순화] SDK 준비 상태 확인하되 복잡한 대기 없이 즉시 체크
      window.__APP_ENVIRONMENT__.piSDKReady = isPiSDKReady();
      resolve(true);
      return;
    }

    // 스크립트 동적 생성
    const script = document.createElement('script');
    script.src = 'https://sdk.minepi.com/pi-sdk.js';
    script.async = true;

    // [네트워크 개선] 초기 빠른 체크 (3초)
    const quickCheck = setTimeout(() => {
      if (!window.Pi) {
        debugLog('[index.js] SDK 로드 지연 감지 - 추가 대기 중...');
        window.__APP_ENVIRONMENT__.slowLoading = true;
      }
    }, 3000);

    // [배포 안정성] 타임아웃 설정 (30초 → 10초로 단축)
    const timeout = setTimeout(() => {
      clearTimeout(quickCheck);
      debugLog('[index.js] Pi SDK 로드 최종 타임아웃 (10초)');
      window.__APP_ENVIRONMENT__.slowLoading = false;

      // 🎯 [NEW] 타임아웃 후 SDK가 뒤늦게 로드되는 경우 대비 (Race Condition 방지)
      // SDK 스크립트 자체는 로딩 중일 수 있으므로, 추가 15초간 감시
      let lateCheckCount = 0;
      const lateCheckInterval = setInterval(() => {
        lateCheckCount++;
        if (window.Pi && !window.__APP_ENVIRONMENT__.piSDKLoaded) {
          clearInterval(lateCheckInterval);
          debugLog('[index.js] Pi SDK 지연 로드 감지 — 초기화 수행');
          window.__APP_ENVIRONMENT__.piSDKLoaded = true;
          window.__APP_ENVIRONMENT__.piSDKReady = isPiSDKReady();
          initializePiSDK();
        }
        if (lateCheckCount > 30) { // 15초 후 감시 중단
          clearInterval(lateCheckInterval);
        }
      }, 500);

      resolve(false);
    }, 10000);

    // 로드 성공 핸들러
    script.onload = () => {
      debugLog('[index.js] Pi SDK 스크립트 로드 완료');

      // SDK 객체 확인 (최대 5초 대기)
      let checkCount = 0;
      const checkInterval = setInterval(() => {
        checkCount++;

        if (window.Pi) {
          clearInterval(checkInterval);
          clearTimeout(quickCheck);
          clearTimeout(timeout);
          debugLog('[index.js] Pi SDK 객체 확인 완료');
          window.__APP_ENVIRONMENT__.piSDKLoaded = true;
          window.__APP_ENVIRONMENT__.slowLoading = false;

          // [단순화] SDK 준비 상태 확인 - 복잡한 대기 로직 제거, 즉시 체크만
          window.__APP_ENVIRONMENT__.piSDKReady = isPiSDKReady();
          debugLog('[index.js] Pi SDK 준비 상태:', window.__APP_ENVIRONMENT__.piSDKReady);

          initializePiSDK();
          resolve(true);

        } else if (checkCount > 50) { // 5초 타임아웃
          clearInterval(checkInterval);
          clearTimeout(quickCheck);
          clearTimeout(timeout);
          errorLog('[index.js] Pi SDK 객체 생성 타임아웃');
          window.__APP_ENVIRONMENT__.slowLoading = false;
          resolve(false);
        }
      }, 100);
    };

    // 로드 실패 핸들러
    script.onerror = (error) => {
      clearTimeout(quickCheck);
      clearTimeout(timeout);
      errorLog('[index.js] Pi SDK 스크립트 로드 실패:', error);
      window.__APP_ENVIRONMENT__.slowLoading = false;
      resolve(false);
    };

    // 스크립트 추가
    document.head.appendChild(script);
  });
};

/**
 * Pi SDK 초기화 함수
 * SDK가 로드된 후에만 실행됨
 * [메인넷 개선] URL 경로에서 App ID 감지 추가
 * [🔧 핵심 수정] sandbox 플래그 로직 개선 - PC 샌드박스 정확히 감지
 */
const initializePiSDK = () => {
  if (!window.Pi) {
    errorLog('[index.js] Pi SDK 초기화 실패 - SDK 없음');
    return;
  }

  // [제거] window.parent/top 조작 제거 - Pi SDK 내부 상태 손상 방지
  try {
    // [2단계 개선] 환경 감지 함수 사용
    const hostname = window.location.hostname;
    const pathname = window.location.pathname;

    // [🔧 핵심 수정] sandbox 플래그는 PC 샌드박스 환경 감지 함수 사용
    // iframe 내부, referrer, ancestorOrigins를 종합적으로 확인
    const isSandbox = isPiSandboxEnvironment();

    // [메인넷 개선] App ID는 앱 타입으로 결정 (메인넷 PiNet 도메인 추가)
    const isMainnetApp = hostname === 'spot.painori.com' ||
      hostname === 'spotnori7417.pinet.com' ||  // ✅ 메인넷 PiNet 도메인 추가
      pathname.includes('spot-nori-58b6af4480fae19f');

    // 환경에 따른 App ID 설정
    const appId = isMainnetApp
      ? 'spot-nori-58b6af4480fae19f'  // 메인넷 App ID
      : 'spot-nori';                   // 테스트넷 App ID

    // [수정] Pi SDK 초기화 시 iframe 감지 로직 분리
    const actualIsInIframe = window.parent !== window;
    const sdkIsInIframe = false; // Pi SDK 초기화용으로만 false 설정

    debugLog('[index.js] Pi SDK 초기화:', {
      version: "2.0",
      sandbox: isSandbox,    // PC 샌드박스 환경 정확히 감지
      appId: appId,          // 앱 타입 기준
      hostname: hostname,
      pathname: pathname,
      isMainnetApp: isMainnetApp,
      referrer: document.referrer ? document.referrer.substring(0, 50) + '...' : '',
      actualIsInIframe: actualIsInIframe,  // 실제 iframe 상태 (디버깅용)
      sdkIsInIframe: sdkIsInIframe        // Pi SDK용 iframe 상태 (false로 강제)
    });

    // 🎯 [Refactor] Use PiAdapter
    PiAdapter.init({
      version: "2.0",
      sandbox: isSandbox,    // PC 샌드박스 환경 정확히 감지
      appId: appId          // 앱 타입 기준
    });

    debugLog('[index.js] Pi SDK 초기화 성공');

    // [단순화] 초기화 후 준비 상태 재확인
    window.__APP_ENVIRONMENT__.piSDKReady = isPiSDKReady();
    debugLog('[index.js] Pi SDK 최종 준비 상태:', window.__APP_ENVIRONMENT__.piSDKReady);

    // Pi SDK 정보 출력 - 개발 환경에서만
    if (isDevelopment()) {
      debugLog('[index.js] Pi SDK 메서드:', Object.keys(window.Pi));
      debugLog('[index.js] Pi environment:', window.Pi.environment);
    }
  } catch (error) {
    errorLog('[index.js] Pi SDK 초기화 오류:', error);
  }
};

/**
 * React 앱 렌더링 함수
 * [변경] ThemeProvider와 GlobalStyle 추가
 */
const renderApp = () => {
  debugLog('[index.js] React 앱 렌더링 시작');

  root.render(
    <React.StrictMode>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <HashRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <App />
        </HashRouter>
      </ThemeProvider>
    </React.StrictMode>
  );

  // 성능 측정
  reportWebVitals();

  debugLog('[index.js] ===== 앱 초기화 완료 =====');
};

/**
 * [2단계 개선] 환경 초기화 함수
 * 환경을 먼저 확정한 후 필요한 경우에만 SDK 로드
 */
const initializeEnvironment = async () => {
  debugLog('[index.js] ===== 환경 초기화 시작 =====');

  // 🚀 [신규] 로그 필터링 활성화 - 사용자 편의성 향상
  enableLogFiltering();

  // 1. 플랫폼 확인
  const platform = getPlatform();
  window.__APP_ENVIRONMENT__.platform = platform;

  // 2. 전체 환경 정보 로깅
  const envInfo = getEnvironmentInfo();
  debugLog('[index.js] 환경 정보:', envInfo);

  // 3. Pi SDK 로드 여부 결정
  if (shouldLoadPiSDK()) {
    debugLog('[index.js] Pi SDK 로드가 필요함');
    const sdkLoaded = await loadPiSDK();
    debugLog('[index.js] Pi SDK 로드 결과:', sdkLoaded ? '성공' : '실패');
  } else {
    debugLog('[index.js] Pi SDK 로드 불필요 - 웹 환경');
  }

  // 4. 환경 초기화 완료 표시
  window.__APP_ENVIRONMENT__.initialized = true;
  // [수정] 마지막 console.log를 debugLog로 변경
  debugLog('[index.js] ===== 환경 초기화 완료 =====');
};

/**
 * [2단계 개선] 앱 초기화 프로세스
 * 1. 환경 초기화 (동기)
 * 2. Pi SDK 로드 (비동기, 필요시)
 * 3. React 앱 렌더링
 */
const initializeApp = async () => {
  debugLog('[index.js] 앱 초기화 프로세스 시작');

  try {
    // 환경 초기화 (SDK 로드 포함)
    await initializeEnvironment();
  } catch (error) {
    errorLog('[index.js] 환경 초기화 중 오류:', error);
    // 오류가 발생해도 앱은 계속 실행
  }

  // [🔧 수정] Analytics는 firebase.js에서 자동 초기화됨

  // 🔧 [신규] 개선된 버전 체크 시스템
  initializeVersionCheck();

  // 환경 초기화 완료 후 앱 렌더링
  renderApp();
};


// 🔧 [신규] 개선된 버전 체크 시스템
// 현재 빌드 버전(.env의 REACT_APP_VERSION) 주입
window.__APP_VERSION__ = process.env.REACT_APP_VERSION;

async function checkAndRefresh() {
  // ▶ 무한 리로드 방지(탭 단위)
  const reloadAttempted = sessionStorage.getItem('version-reload-attempted');
  if (reloadAttempted) {
    console.log('[VersionCheck] reload already attempted — skip');
    return;
  }

  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      // 5초 타임아웃 (Chrome/Edge/FF 최신 지원)
      signal: AbortSignal.timeout(5000),
    });
    if (!res.ok) {
      console.warn('[VersionCheck] HTTP error:', res.status);
      return;
    }

    const data = await res.json();
    if (!data || !data.version) {
      console.error('[VersionCheck] invalid response:', data);
      return;
    }

    const serverVersion = data.version;
    const clientVersion = window.__APP_VERSION__ || '0.0.0';
    console.log('[VersionCheck]', { server: serverVersion, client: clientVersion });

    if (serverVersion !== clientVersion) {
      console.log('[VersionCheck] new version available:', { server: serverVersion, client: clientVersion });
      // 🔧 [제거] 자동 새로고침 기능 제거 - 사용자가 직접 새로고침하거나 새 방문 시 최신 버전 적용
      // sessionStorage 플래그도 제거 (자동 새로고침 없으므로 불필요)
    } else {
      // 최신 버전
      sessionStorage.removeItem('version-reload-attempted');
    }
  } catch (e) {
    if (e?.name === 'AbortError') {
      console.warn('[VersionCheck] timeout (5s)');
    } else {
      console.error('[VersionCheck] error:', e);
    }
  }
}

function initializeVersionCheck() {
  // 앱 시작 시 1회만 체크
  checkAndRefresh();

  // 🔧 [제거] 주기적 버전 체크 제거 - 자동 새로고침 없으므로 불필요
  // 리소스 최적화 및 불필요한 네트워크 트래픽 제거
}

// Service Worker 등록은 public/index.html에서 처리됨

// 앱 시작
initializeApp();